import * as React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import {
  ContentFilterLinkedInController,
  ContentFilterLinkedInControllerProps
} from './ContentFilterLinkedIn.controller';
import { UseFormReturn } from 'react-hook-form';
import {
  LinkedInSearchContentType,
  LinkedInSearchSortType,
  LinkedInSearchType,
  SocialProfileFields_LinkedInSocialProfile_Fragment,
  useSearchLinkedInPostsLazyQuery
} from '../../../../models/graphql';
import { gql, useApolloClient } from '@apollo/client';
import { FindContentContext, SearchStepEnum } from '../FindContentProvider';
import { ScrollContext } from '../../../../providers/ScrollProvider';

export type SearchLinkedInPostsVariables = {
  socialProfileId: string;
  searchType: LinkedInSearchType;
  contentType: LinkedInSearchContentType;
  sortType: LinkedInSearchSortType;
  externalId: string;
  term?: string;
};

export type ContentFilterLinkedInApolloProps = {
  formInstance: UseFormReturn;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
};

gql`
  query SearchLinkedInPosts(
    $socialProfileId: ID!
    $searchType: LinkedInSearchType!
    $contentType: LinkedInSearchContentType!
    $sortType: LinkedInSearchSortType!
    $externalId: String!
    $term: String
    $cursor: String
  ) {
    searchLinkedInPosts(
      socialProfileId: $socialProfileId
      searchType: $searchType
      contentType: $contentType
      sortType: $sortType
      externalId: $externalId
      term: $term
      cursor: $cursor
    ) {
      cursor
      items {
        ...ThirdPartyPostFields
      }
    }
  }
`;

export const ContentFilterLinkedInApollo = (props: ContentFilterLinkedInApolloProps): JSX.Element => {
  const { cache } = useApolloClient();
  const [searchParameters, setSearchParameters] = useState<SearchLinkedInPostsVariables | undefined>(undefined);
  const [searchLinkedInPosts, linkedInPostsResults] = useSearchLinkedInPostsLazyQuery({ fetchPolicy: 'network-only' });
  const [reachedLimit, setReachedLimit] = useState<boolean>(false);

  const { data: posts, fetchMore } = linkedInPostsResults;
  const {
    setLoading,
    setLoadingMore,
    setPosts,
    setSelectedPosts,
    setSearchStep,
    setHasSearched,
    onSearchResult,
    profiles
  } = useContext(FindContentContext);
  const { setOnScrollBottom } = useContext(ScrollContext);

  const fetchLinkedInPosts = useCallback(
    async (parameters: SearchLinkedInPostsVariables) => {
      setLoading(true);
      const linkedinProfiles = profiles.filter(
        p => p.__typename === 'LinkedInSocialProfile'
      ) as SocialProfileFields_LinkedInSocialProfile_Fragment[];
      onSearchResult(parameters, linkedinProfiles, false);
      await searchLinkedInPosts({ variables: parameters })
        .then(results => {
          if (results.error) {
            throw results.error;
          } else if (results.data?.searchLinkedInPosts.items.length) {
            setSearchStep(SearchStepEnum.TWO);
          }

          setHasSearched(true);
        })
        .finally(() => setLoading(false));
    },
    [searchLinkedInPosts, setHasSearched, setLoading, setSearchStep, profiles, onSearchResult]
  );

  const purgeLinkedInPostsCache = useCallback(() => {
    cache.modify({
      fields: {
        searchLinkedInPosts() {
          return {
            cursor: '',
            items: []
          };
        }
      }
    });
  }, [cache]);

  const purgeLinkedInCache = useCallback(() => {
    purgeLinkedInPostsCache();

    setPosts([]);
    setSelectedPosts([]);
    setReachedLimit(false);
  }, [purgeLinkedInPostsCache, setPosts, setSelectedPosts]);

  useEffect(() => {
    if (posts?.searchLinkedInPosts.items) {
      setPosts(posts.searchLinkedInPosts.items);
    }
  }, [posts, setPosts]);

  useEffect(() => {
    setOnScrollBottom(() => {
      // Don't proceed if we already reached limit
      if (!reachedLimit && !!searchParameters?.socialProfileId) {
        setLoadingMore(true);

        const cursor = posts?.searchLinkedInPosts.cursor;
        fetchMore({ variables: { ...searchParameters, cursor } })
          .then(results => {
            const newItems = results.data.searchLinkedInPosts.items;
            // If no results, we should not fetch more
            setReachedLimit(newItems.length === 0);
            setPosts(prev => [...prev, ...newItems]);
          })
          .finally(() => {
            setLoadingMore(false);
          });
      }
    });
  }, [fetchMore, posts, reachedLimit, searchParameters, setLoadingMore, setOnScrollBottom, setPosts]);

  const controllerProps: ContentFilterLinkedInControllerProps = {
    ...props,
    searchParameters,
    setSearchParameters,
    fetchLinkedInPosts,
    purgeLinkedInCache
  };

  return <ContentFilterLinkedInController {...controllerProps} />;
};
